import { useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { Button, ConfirmDialog, FormField, Input, Text } from "@lagoon/ui";

/**
 * An action that cannot be taken without saying why.
 *
 * Deliberately two steps, as it always was. The first click opens a dialog
 * rather than performing anything, so no destructive action is ever one stray
 * click away; the second confirms it. What changed with the design library is
 * the second step: it used to be an inline form that pushed the page around,
 * and it is now a modal `ConfirmDialog`, which is labelled, traps focus, and
 * puts the safe option first in the tab order.
 *
 * Shared by every screen rather than copied into each. The reason field is the
 * console's one universal habit, and a second copy is a second place for it to
 * quietly stop being required.
 *
 * The confirm button stays ENABLED with an empty reason and validates on
 * press. `ConfirmDialog` has no way to disable confirm alone: its `pending`
 * disables cancel too, which would trap somebody who opened the dialog by
 * mistake: so refusing on press and saying why in a `role="alert"` is both
 * the accessible option and the one that explains itself.
 */

/**
 * One value the action needs besides the reason.
 *
 * There is at most one, deliberately. An action that needs a form is a form,
 * and belongs in a dialog of its own (`ProvisionOrg` is one); an action that
 * needs a single address, a quota or a flag value is still one decision with
 * one field, and sharing this keeps the reason habit in one place.
 */
export interface ReasonField {
  label: string;
  type?: "text" | "email" | "number";
  placeholder?: string;
  hint?: string;
  /** What the field opens with. A quota or a flag opens on its current value. */
  initialValue?: string;
  /** A message when the value will not do, null when it will. */
  validate?: (value: string) => string | null;
}

export function ReasonAction({
  label,
  hint,
  danger = false,
  compact = false,
  field,
  onSubmit,
}: {
  label: string;
  hint?: string;
  danger?: boolean;
  compact?: boolean;
  field?: ReasonField;
  onSubmit: (reason: string, value?: string) => Promise<void>;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [value, setValue] = useState(field?.initialValue ?? "");
  const [problem, setProblem] = useState<{ field: "value" | "reason"; text: string } | null>(null);

  const run = useMutation({
    mutationFn: () => onSubmit(reason.trim(), field === undefined ? undefined : value.trim()),
    onSuccess: () => {
      setOpen(false);
      setReason("");
      setValue(field?.initialValue ?? "");
      setProblem(null);
    },
  });

  const confirm = (): void => {
    const wrong = field?.validate?.(value.trim()) ?? null;
    if (wrong !== null) {
      setProblem({ field: "value", text: wrong });
      return;
    }
    if (reason.trim().length < 3) {
      setProblem({
        field: "reason",
        text: "Say why. This is what an auditor reads in six months.",
      });
      return;
    }
    setProblem(null);
    run.mutate();
  };

  return (
    <>
      {/*
        A destructive TRIGGER is outlined, not filled. Pressing it opens a
        dialog and changes nothing, and a row of solid red pills on a screen
        where three of the four actions are dangerous reads as decoration
        rather than as a warning. The filled red button is the one inside the
        dialog, which is the one that actually does it.
      */}
      <Button
        variant={danger ? "outline" : compact ? "ghost" : "outline"}
        size={compact ? "sm" : "md"}
        className={
          danger
            ? "lg:border-destructive/55 lg:text-destructive lg:hover:border-destructive lg:hover:text-destructive"
            : undefined
        }
        onClick={() => {
          setOpen(true);
        }}
      >
        {label}
      </Button>

      <ConfirmDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setProblem(null);
        }}
        title={label}
        description={hint}
        confirmLabel={run.isPending ? "Working…" : label}
        onConfirm={confirm}
        destructive={danger}
        pending={run.isPending}
      >
        <div className="lg:flex lg:flex-col lg:gap-4">
          {field !== undefined && (
            <FormField
              label={field.label}
              required
              {...(field.hint === undefined ? {} : { hint: field.hint })}
              {...(problem?.field === "value" ? { error: problem.text } : {})}
            >
              <Input
                type={field.type ?? "text"}
                value={value}
                autoFocus
                {...(field.placeholder === undefined ? {} : { placeholder: field.placeholder })}
                onChange={(event) => {
                  setValue(event.target.value);
                }}
              />
            </FormField>
          )}

          <FormField
            label="Reason"
            required
            hint="Recorded in the audit trail, against your name."
            {...(problem?.field === "reason" ? { error: problem.text } : {})}
          >
            <Input
              value={reason}
              placeholder="Why this is happening"
              autoFocus={field === undefined}
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
          </FormField>

          {run.error && (
            <Text size="sm" tone="danger" role="alert">
              {run.error.message}
            </Text>
          )}
        </div>
      </ConfirmDialog>
    </>
  );
}
